import { COMPETITOR_MAP_MAX_ZOOM } from '@/core/components/reputation/competitor-map-camera';
import { haversineMeters } from '@/core/components/reputation/competitor-radius';

/** Anything with a real position: a nearby place, a tracked competitor, or "you". */
export type ClusterablePlace = {
  placeId: string;
  latitude: number;
  longitude: number;
  /** Your own location — may be absorbed into a cluster like any other pin. */
  isOwn?: boolean;
};

export type CompetitorPinGroup<T extends ClusterablePlace> =
  | {
      kind: 'pin';
      id: string;
      item: T;
      latitude: number;
      longitude: number;
    }
  | {
      kind: 'cluster';
      id: string;
      members: T[];
      latitude: number;
      longitude: number;
      includesOwn: boolean;
    };

/**
 * Pins closer than this (screen pixels) read as one blob and are clustered.
 * A chip is ~70px wide, so this keeps neighbouring chips from overlapping.
 */
export const COMPETITOR_CLUSTER_PIXELS = 68;

export function metersPerPixel(latitude: number, zoom: number): number {
  const latRad = (latitude * Math.PI) / 180;
  const pixelsAtZoom = 256 * 2 ** zoom;
  return (Math.cos(latRad) * 40_075_016.686) / pixelsAtZoom;
}

export function pixelDistance(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
  zoom: number,
): number {
  const meters = haversineMeters(a, b);
  const mpp = metersPerPixel((a.latitude + b.latitude) / 2, zoom);
  if (mpp <= 0) return Number.POSITIVE_INFINITY;
  return meters / mpp;
}

export function clusterGroupId(
  members: readonly Pick<ClusterablePlace, 'placeId'>[],
): string {
  return members
    .map((member) => member.placeId)
    .sort()
    .join('|');
}

/** Geographic mean of members — cluster marker + click-zoom target. */
export function clusterCentroid(members: readonly ClusterablePlace[]): {
  latitude: number;
  longitude: number;
} {
  let latitude = 0;
  let longitude = 0;
  for (const member of members) {
    latitude += member.latitude;
    longitude += member.longitude;
  }
  const n = Math.max(1, members.length);
  return { latitude: latitude / n, longitude: longitude / n };
}

/** Next integer zoom toward a cluster, or `null` when already at max. */
export function nextZoomTowardCluster(
  currentZoom: number,
  maxZoom = COMPETITOR_MAP_MAX_ZOOM,
): number | null {
  if (!Number.isFinite(currentZoom)) return null;
  const next = Math.min(maxZoom, Math.floor(currentZoom) + 1);
  if (next <= currentZoom) return null;
  return next;
}

/**
 * Group pins that would overlap at this zoom. Pins are never moved: a lone
 * pin keeps its real position, and only a cluster marker sits at an aggregate
 * position (your own spot when "you" are in it, else the members' centroid).
 *
 * Greedy leader clustering, own first so nearby peers gather around you rather
 * than drifting off, then by id for stable results between renders.
 */
export function groupCompetitorPins<T extends ClusterablePlace>(
  items: readonly T[],
  zoom: number,
  pixelThreshold = COMPETITOR_CLUSTER_PIXELS,
): CompetitorPinGroup<T>[] {
  const ordered = [...items].sort(
    (a, b) =>
      Number(Boolean(b.isOwn)) - Number(Boolean(a.isOwn)) ||
      a.placeId.localeCompare(b.placeId),
  );
  const assigned = new Set<string>();
  const groups: CompetitorPinGroup<T>[] = [];

  for (const leader of ordered) {
    if (assigned.has(leader.placeId)) continue;
    const members = ordered.filter(
      (candidate) =>
        !assigned.has(candidate.placeId) &&
        (candidate === leader ||
          pixelDistance(leader, candidate, zoom) <= pixelThreshold),
    );
    for (const member of members) assigned.add(member.placeId);

    if (members.length === 1) {
      groups.push({
        kind: 'pin',
        id: leader.placeId,
        item: leader,
        latitude: leader.latitude,
        longitude: leader.longitude,
      });
      continue;
    }
    const own = members.find((member) => member.isOwn);
    const anchor = own ?? clusterCentroid(members);
    groups.push({
      kind: 'cluster',
      id: clusterGroupId(members),
      members,
      latitude: anchor.latitude,
      longitude: anchor.longitude,
      includesOwn: Boolean(own),
    });
  }
  return groups;
}
